// PORTFOLIO (/portfolio) — 18개 프로젝트 카드 · 분야/연도 필터 · 상세 펼침 · 사진 확대보기
// 디자인 시안 HTML을 그대로 옮긴 화면입니다. 문구·사진·구성은 시안과 같게 유지해 주세요.
// 데이터: src/data/portfolio.js
// 주소 파라미터: ?project=<id> (해당 상세가 열린 채 시작) · ?category=trade|digital|events (분야 필터)
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import DATA from "../data/portfolio";
import { useLightbox } from "../components/Lightbox";
import { usePageMotion } from "../motion/usePageMotion";
import { reducedMotion, scrollToTarget } from "../motion/smoothScroll";

const CATEGORIES = ["ALL", "TRADE", "DIGITAL", "EVENTS"];
const YEARS = ["2026", "2025", "2023", "2021", "2020", "2019", "2018", "2017"];
const TYPE_LABEL = {
  photo: "현장 사진",
  capture: "채널·콘텐츠 캡처",
  material: "홍보물·콘텐츠",
  composite: "편집 이미지 · 실제 자료 조합",
  editorial: "편집 비주얼 · 현장 사진 아님",
};
const MOBILE_MAX = 760; // 이 폭 이하에서 카드 1열
const pad2 = (n) => String(n).padStart(2, "0");
const isMobile = () => window.innerWidth <= MOBILE_MAX;

export default function PortfolioPage() {
  const location = useLocation();
  // 주소의 ?project= / ?category= 가 바뀌면 처음 상태부터 다시 그린다
  return <Portfolio key={location.search} search={location.search} />;
}

function Portfolio({ search }) {
  const initial = useMemo(() => {
    const params = new URLSearchParams(search);
    const cat = (params.get("category") || "").toUpperCase();
    const project = Number(params.get("project"));
    return {
      cat: CATEGORIES.includes(cat) && cat !== "ALL" ? cat : "ALL",
      selected: DATA.some((p) => p.id === project) ? project : null,
    };
  }, [search]);

  const [activeCat, setActiveCat] = useState(initial.cat);
  const [activeYear, setActiveYear] = useState("ALL");
  const [selected, setSelected] = useState(initial.selected);
  const [mobile, setMobile] = useState(isMobile);
  const scrollRequest = useRef(initial.selected !== null ? "initial" : null);
  const lightbox = useLightbox();
  const mainRef = useRef(null);
  const busy = useRef(false);
  const [filtered, setFiltered] = useState(false);

  // 등장 효과와 제목 단어별 등장
  usePageMotion(mainRef, {
    split: ["#portfolio-title"],
    reveal: [".intro-left .eyebrow", ".intro-lead", ".intro-signal", ".intro-right figure", ".intro-status > div", ".p-card .card-btn"],
  });

  // 760px 경계를 넘으면 상세 위치(1열/2열) 다시 계산
  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const items = DATA.filter(
    (p) => (activeCat === "ALL" || p.cat === activeCat) && (activeYear === "ALL" || String(p.sortYear) === activeYear)
  );
  const idx = items.findIndex((x) => x.id === selected);
  const current = idx >= 0 ? items[idx] : null;
  const cols = mobile ? 1 : 2;
  // 상세는 선택한 카드가 있는 줄의 마지막 카드 바로 뒤에 들어간다
  const rowStart = idx >= 0 ? Math.floor(idx / cols) * cols : -1;
  const detailAfter = idx >= 0 ? rowStart + Math.min(cols - 1, items.length - 1 - rowStart) : -1;

  // 상세가 열리면 그 위치로 이동
  useEffect(() => {
    const req = scrollRequest.current;
    if (!req || selected === null) return undefined;
    const el = document.getElementById("selectedProject");
    if (!el) return undefined;
    const go = () => {
      scrollRequest.current = null;
      scrollToTarget(el);
    };
    if (req === "initial" && document.readyState !== "complete") {
      // 주소로 처음 들어온 경우: 페이지가 다 불러와진 뒤 이동
      window.addEventListener("load", go, { once: true });
      return () => window.removeEventListener("load", go);
    }
    const raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [selected]);

  // 열린 상세를 0.32초 동안 접은 뒤 then 실행(15번). 접히는 동안 아래 카드가 천천히 올라온다.
  // 상세 위아래의 줄 간격까지 함께 접어, 다 접힌 순간 툭 튀지 않게 한다.
  const collapseDetail = (then) => {
    const el = document.getElementById("selectedProject");
    if (!el || reducedMotion() || !el.animate) {
      then();
      return;
    }
    busy.current = true;
    const cs = getComputedStyle(el);
    const gap = parseFloat(getComputedStyle(el.parentElement).rowGap) || 0;
    el.style.overflow = "hidden";
    const anim = el.animate(
      [
        { height: `${el.getBoundingClientRect().height}px`, opacity: 1, marginTop: cs.marginTop, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom },
        { height: "0px", opacity: 0, marginTop: `${-gap}px`, paddingTop: "0px", paddingBottom: "0px" },
      ],
      { duration: 320, easing: "cubic-bezier(.4,0,.2,1)", fill: "forwards" }
    );
    anim.onfinish = () => {
      busy.current = false;
      then();
    };
  };

  const toggle = (id) => {
    if (busy.current) return;
    if (selected === id) {
      closeDetail();
      return;
    }
    const open = () => {
      scrollRequest.current = "click";
      setSelected(id);
    };
    if (current) collapseDetail(open);
    else open();
  };
  const closeDetail = () => {
    if (busy.current || selected === null) return;
    const id = selected;
    collapseDetail(() => {
      setSelected(null);
      // 방금 보던 카드로 돌아간다
      requestAnimationFrame(() => {
        const card = document.querySelector(`.p-card[data-id="${id}"]`);
        if (card) scrollToTarget(card);
      });
    });
  };
  const chooseCat = (cat) => {
    setActiveCat(cat);
    setSelected(null);
    setFiltered(true);
  };
  const chooseYear = (e) => {
    setActiveYear(e.target.value);
    setSelected(null);
    setFiltered(true);
  };
  const openLightbox = (p, index, opener) => {
    const media = [p.hero, ...(p.gallery || [])];
    lightbox.open(
      media.map((m) => ({
        src: m.full,
        thumb: m.thumb,
        w: m.w,
        h: m.h,
        cap: m.cap,
        meta: TYPE_LABEL[m.type] + " · " + p.title,
      })),
      index,
      opener
    );
  };

  return (
    <>
      <main ref={mainRef}>
        <section aria-labelledby="portfolio-title" className="intro intro-new" id="portfolioTop">
          <div className="wrap intro-stage">
            <div className="intro-left">
              <div className="eyebrow">PORTFOLIO / SELECTED WORK</div>
              <h1 id="portfolio-title">계획을 넘어,<br />{"현장에서 "}<em>완성한 일들.</em></h1>
              <p className="intro-lead">태국 시장 진출과 유통, 디지털 마케팅, 현장 행사 운영까지.<br />INBA Thailand가 수행해 온 프로젝트를 소개합니다.</p>
              <div className="intro-signal"><span>INBA THAILAND</span><i /><span>2017—2026</span></div>
            </div>
            <div className="intro-right" aria-label="INBA 수행 프로젝트 자료 사진 모음">
              <figure className="mosaic-main">
                <img style={{ objectPosition: "50% 50%" }} src="/assets/img/photos/pf-intro-main.jpg" alt="대구국제뷰티엑스포 태국관 INBA 부스 현장 인터뷰" />
                <figcaption>FIELD EXECUTION</figcaption>
              </figure>
              <figure className="mosaic-top">
                <img style={{ objectPosition: "50% 45%" }} src="/assets/img/photos/pf-18-g4.jpg" alt="Pet Fair SE Asia 2026 한국관 라이브 방송" />
              </figure>
              <figure className="mosaic-bottom">
                <img style={{ objectPosition: "50% 50%" }} src="/assets/img/photos/pf-14-hero.jpg" alt="2018 K-Beauty Expo 태국 시장 진출 설명회" />
              </figure>
              <span className="mosaic-vertical">KOREA × THAILAND</span>
            </div>
            <div className="intro-status">
              <div><b>18</b><span>SELECTED PROJECTS</span></div>
              <div><b>03</b><span>BUSINESS AREAS</span></div>
              <div className="intro-cta">{"SCROLL TO EXPLORE "}<span>↓</span></div>
            </div>
          </div>
        </section>
        <div className="filter-bar" id="portfolio">
          <div className="wrap filters">
            <div className="filter-buttons" id="categoryFilters" aria-label="사업별 프로젝트 필터">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={activeCat === cat ? "active" : undefined}
                  data-cat={cat}
                  aria-pressed={activeCat === cat ? "true" : "false"}
                  onClick={() => chooseCat(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="filter-meta">
              <span className="count"><strong id="visibleCount">{items.length}</strong>{` / ${DATA.length} PROJECTS`}</span>
              <div className="year-wrap">
                <label htmlFor="yearFilter">YEAR</label>
                <select id="yearFilter" aria-label="연도별 프로젝트 필터" value={activeYear} onChange={chooseYear}>
                  <option value="ALL">ALL YEARS</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <section className="portfolio wrap" aria-label="프로젝트 카드 목록">
          {/* 필터를 바꾸면 목록을 새로 그리며 살짝 떠오르게 한다(16번) */}
          <div id="cards" key={`${activeCat}|${activeYear}`} className={"cards" + (filtered ? " is-filtering" : "")}>
            {items.length === 0 ? (
              <div className="empty">
                <h3>해당 조건의 프로젝트가 없습니다.</h3>
                <p>다른 사업 분야 또는 연도를 선택해 주세요.</p>
              </div>
            ) : (
              items.map((p, i) => (
                <Fragment key={p.id}>
                  <ProjectCard project={p} number={DATA.indexOf(p) + 1} open={selected === p.id} onToggle={() => toggle(p.id)} />
                  {current && i === detailAfter && (
                    <ProjectDetail
                      key={`detail-${current.id}`}
                      project={current}
                      number={DATA.indexOf(current) + 1}
                      onClose={closeDetail}
                      onZoom={(index, opener) => openLightbox(current, index, opener)}
                    />
                  )}
                </Fragment>
              ))
            )}
          </div>
        </section>
      </main>
    </>
  );
}

function ProjectCard({ project: p, number, open, onToggle }) {
  return (
    <article className={"p-card v6-card" + (open ? " selected" : "")} data-id={p.id}>
      <button className="card-btn" type="button" aria-expanded={open} aria-label={`${p.title} 상세 보기`} onClick={onToggle}>
        <span className="photo">
          <img
            src={p.cover.src}
            style={{ objectPosition: p.cover.pos }}
            data-photo-slot={`project-${pad2(p.id)}-cover`}
            alt={`${p.title} 대표 이미지`}
            loading="lazy"
          />
          <span className="card-number">{`${pad2(number)} / ${DATA.length}`}</span>
          <span className="photo-arrow" aria-hidden="true">↗</span>
        </span>
        <span className="card-content">
          <span className="card-line">
            <span className="cat">{p.cat}</span>
            <span className="sep" />
            <span>{p.year}</span>
          </span>
          <h2>{p.title}</h2>
          <p className="card-subtitle">{p.subtitle}</p>
          <div className="card-meta">{p.meta}</div>
        </span>
      </button>
    </article>
  );
}

function ProjectDetail({ project: p, number, onClose, onZoom }) {
  const h = p.hero;
  const gal = p.gallery || [];
  const metrics = p.metrics || [];
  const mcols = metrics.length === 4 ? 2 : Math.max(1, metrics.length);
  const links = p.links || [];
  return (
    <section className="detail-panel v6" id="selectedProject" tabIndex={-1} aria-label={`${p.title} 상세`}>
      <div className="v6-top">
        <div className="v6-crumb">
          <span className="v6-cat">{p.cat}</span>
          <i />
          <span>{p.year}</span>
          <i />
          <span>{`PROJECT ${pad2(number)} / ${DATA.length}`}</span>
        </div>
        <button type="button" className="detail-close" onClick={onClose}>
          {"목록으로 돌아가기 "}
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="v6-main">
        <figure className={"v6-hero" + (h.type === "editorial" ? " is-editorial" : "")}>
          <button type="button" className="v6-zoom" data-i="0" aria-label="대표 이미지 크게 보기" onClick={(e) => onZoom(0, e.currentTarget)}>
            <img src={h.src} style={{ objectPosition: h.pos }} alt={h.cap} />
            <span className="v6-zoom__icon" aria-hidden="true">⤢</span>
          </button>
          <figcaption>
            <span className={`v6-type v6-type--${h.type}`}>{TYPE_LABEL[h.type]}</span>
            <span>{h.cap}</span>
          </figcaption>
        </figure>
        <div className="v6-info">
          <h3>{p.title}</h3>
          <p className="v6-lead">{p.subtitle}</p>
          <p className="v6-desc">{p.description}</p>
          <div className="v6-block">
            <div className="v6-label">KEY EXECUTION · 주요 수행내용</div>
            <ul>
              {(p.work || []).slice(0, 3).map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
          <div className="v6-block">
            <div className="v6-label">{metrics.length ? "KEY RESULTS · 핵심 성과" : "PROJECT PROFILE · 프로젝트 특성"}</div>
            {metrics.length > 0 && (
              <div className="v6-metrics" style={{ gridTemplateColumns: `repeat(${mcols},minmax(0,1fr))` }}>
                {metrics.map((m, i) => (
                  <div className="v6-metric" key={i}>
                    <strong>{m[0]}</strong>
                    <span>{m[1]}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="v6-tags">
              {(p.tags || []).map((t, i) => (
                <span key={i}>{t}</span>
              ))}
            </div>
          </div>
          {p.related && (
            <div className="v6-related">
              <b>연계 수행 사례</b>
              <span>{p.related}</span>
            </div>
          )}
          {links.length > 0 && (
            <div className="v6-links">
              {links.map(([name, href], i) => (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer">
                  {name + " "}
                  <span aria-hidden="true">↗</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      {gal.length > 0 && (
        <div className="v6-gallery">
          <div className="v6-gallery__head">
            <div className="v6-label">{`PROJECT GALLERY · ${pad2(gal.length)}`}</div>
            <span>사진을 누르면 크게 볼 수 있습니다</span>
          </div>
          <div className="v6-grid">
            {gal.map((g, i) => (
              <button
                key={i}
                type="button"
                className="v6-thumb"
                data-i={i + 1}
                aria-label={`${g.cap} 크게 보기`}
                onClick={(e) => onZoom(i + 1, e.currentTarget)}
              >
                <span className="v6-thumb__img">
                  <img src={g.thumb} style={{ objectPosition: g.pos }} alt={g.cap} loading="lazy" />
                </span>
                <span className="v6-thumb__cap">{g.cap}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="v6-note">{p.note || ""}</p>
    </section>
  );
}
